import { Box, Card, Flex, Text } from '@mantine/core';
import { GetEnergyConsumptionResponse } from 'api/actions/get-energy-consumption/get-energy-consumption-response';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { xScaleConfig, yGridConfig, barDatasetBase, formatEur4 } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type SpotPrice = NonNullable<GetEnergyConsumptionResponse['spotPrices']>[number];

interface SpotCostChartCardProps {
  /** Cost per interval at spot prices (€), null where no match was found */
  costData: (number | null)[];
  /** Labels for valid (non-null consumption) intervals */
  validIntervalLabels: (string | undefined)[];
  /** Total spot cost for the day */
  totalCost: number | null;
  /** Raw spot prices for average calculation */
  spotPrices: SpotPrice[];
  /** The date string from the API response */
  date: string;
  /** Shared Y-axis max with the fixed tariff chart */
  sharedCostYMax: number | undefined;
}

/**
 * Displays a bar chart of electricity costs calculated using real-time spot prices.
 *
 * @prop {(number | null)[]} costData - Per-interval spot costs in €.
 * @prop {(string | undefined)[]} validIntervalLabels - X-axis labels.
 * @prop {number | null} totalCost - Total spot cost in €.
 * @prop {SpotPrice[]} spotPrices - Raw spot price list for average display.
 * @prop {string} date - ISO date string for the chart title.
 * @prop {number | undefined} sharedCostYMax - Shared Y maximum with the FIX chart.
 */
export default function SpotCostChartCard({
  costData,
  validIntervalLabels,
  totalCost,
  spotPrices,
  date,
  sharedCostYMax,
}: SpotCostChartCardProps) {
  const { _t } = useLang();
  const chartData = useMemo(
    () => ({
      labels: validIntervalLabels,
      datasets: [
        {
          ...barDatasetBase,
          label: _t('Price (€)'),
          data: costData.filter((c) => c !== null),
          backgroundColor: '#FF6B35',
          hoverBackgroundColor: '#E55A2B',
        },
      ],
    }),
    [_t, validIntervalLabels, costData]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...xScaleConfig },
        y: {
          ticks: { color: theme.colors?.gray?.[7], callback: formatEur4 },
          grid: yGridConfig,
          beginAtZero: true,
          max: sharedCostYMax,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: { label: (ctx: TooltipItem<'bar'>) => `${_t('Price')}: ${ctx.parsed.y.toFixed(6)} €` },
        },
      },
    }),
    [_t, sharedCostYMax]
  );

  const averageSpotPrice = spotPrices.length
    ? (spotPrices.reduce((sum, sp) => sum + (sp.price || 0), 0) / spotPrices.length).toFixed(2)
    : '0';

  return (
    <Card p={20} radius={10} shadow="sm" mt={20}>
      <H2SemiBold mb={20}>
        {_t('Price at spot prices')} - {new Date(date).toLocaleDateString('sk-SK')}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#FFF4F0', borderRadius: 8, border: '1px solid #FFE0D6' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Total daily price (spot)')}:
          </Text>
          <Text size="xl" fw={700} c="#FF6B35">
            {totalCost !== null ? totalCost.toFixed(4) : '0.0000'} €
          </Text>
        </Flex>
        <Text size="sm" c="gray.6" mt={5}>
          {_t('Average price')}: {averageSpotPrice} €/MWh
        </Text>
      </Box>
    </Card>
  );
}
